import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  Tag,
  Building,
  Bookmark,
  ShoppingCart,
} from "lucide-react"; // Added Building
import { motion } from "framer-motion";
import PartChatDialog from "@/components/PartChatDialog";
import StarRating from "@/components/ui/StarRating";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead,
} from "../../store/partChat/partChatActions";
import { openAuthDialog } from "../../store/auth/authSlice";
import { toast } from "../ui/use-toast";
import {
  fetchSavedParts,
  removeSavedPart,
  savePart,
} from "../../store/parts/partsAction";
import PartImageSlider from "../../hooks/partImageSlider";
import PartPaymentModal from "../Modal/PartPaymentModal";
import { useNavigate } from "react-router-dom";

const SuggestedPartCard = ({
  part,
  variants,
  onOpenChange: controlledOnOpenChange,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { messages, isMessagesLoading, isSenderLoading } = useSelector(
    (state) => state.partChat,
  );
  const { saveLoading, savedParts, removeLoading } = useSelector(
    (state) => state.parts,
  );
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;
  const [selectedConversation, setSelectedConversation] = useState(null);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const isSaved = savedParts?.some(
    (saved) => saved.partId === part?.id && saved.userId === user?.uid,
  );

  if (!part) {
    console.warn("Part data is null or undefined");
    return null;
  }

  const handleChatClick = async () => {
    if (!isAuthenticated || !user) {
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login to send a message to Seller.",
          variant: "destructive",
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: true,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: () => setIsOpen(true),
          }),
        );
      }
      return;
    }

    try {
      const sellerData = await dispatch(
        fetchSellerById({ userId: part?.sellerId }),
      ).unwrap();

      const conversation = {
        partInfo: part,
        otherUserDetail: sellerData,
      };

      dispatch(
        fetchMessages({
          buyerId: user?.uid,
          sellerId: part?.sellerId,
          partId: part?.id,
          currentUserId: user?.uid,
        }),
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: user?.uid,
            receiverId: part?.sellerId,
            partId: part?.id,
          }),
        );
      }, 1000);

      setSelectedConversation(conversation);
    } catch (error) {
      console.error("❌ Failed to fetch seller:", error);
    }
  };

  const tierStyles = {
    featured: {
      icon: Tag,
      label: "Featured",
      bgColor: "bg-yellow-100 dark:bg-yellow-100",
      textColor: "text-yellow-700 dark:text-yellow-800",
      borderColor: "border-yellow-300 dark:border-yellow-800",
    },
  };

  const getCurrentTier = (part) => {
    if (part?.isFeatured) return tierStyles.featured;
    return null;
  };

  const handleBuyClick = () => {
    if (!isAuthenticated || !user) {
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login as buyer to buy Parts.",
          variant: "destructive",
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: true,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: () => setIsOpen(true),
          }),
        );
      }
      return;
    }

    if (!part?.stripePaymentLink) {
      toast({
        title: "Payment Unavailable",
        description: "This seller has not added a Stripe payment link.",
        variant: "destructive",
      });
      return;
    }
    setShowPaymentModal(true);
  };

  const tier = getCurrentTier(part);

  const displayPrice =
    typeof part?.price === "number"
      ? part?.price.toFixed(2)
      : typeof part?.price === "string" && !isNaN(parseFloat(part?.price))
        ? parseFloat(part?.price).toFixed(2)
        : "N/A";

  const getDisplayName = (item) => {
    const name =
      item?.name?.toLowerCase() === "other" ? `${item?.sub_name}` : item?.name;
    if (!name) return "";
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const handleSaveClick = (part) => {
    if (!isAuthenticated || !user) return;

    if (isSaved) {
      dispatch(removeSavedPart({ userId: user?.uid, partId: part?.id }));
    } else {
      dispatch(savePart({ userId: user?.uid, part: part }));
    }
  };

  const getDisplayMakeModel = (item) => {
    if (!item) return "";

    const make = item?.make || "";
    const subMake = item?.sub_make || "";
    const model = item?.model || "";
    const subModel = item?.sub_model || "";

    // Check if either is "other"
    const isOther =
      make?.toLowerCase() === "other" || model?.toLowerCase() === "other";

    // Resolve actual values (prefer sub_ fields if main is "other" and sub exists)
    const displayMake =
      make?.toLowerCase() === "other" && subMake ? subMake : make;
    const displayModel =
      model?.toLowerCase() === "other" && subModel ? subModel : model;

    // Combine and trim
    let result = `${displayMake} ${displayModel}`.trim();

    // Capitalize each word
    result = result
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

    // If either was "other", append (Other) at the end
    if (isOther) {
      result += " (Other)";
    }

    return result;
  };

  const partName = getDisplayName(part) || "Unknown Part";
  const partYear = part?.year || "N/A";
  const partMakeModel = getDisplayMakeModel(part) || "Unknown";
  const sellerName = part?.sellerName || "Unknown Seller";
  const sellerRating = part?.sellerRating || 0;
  const reviewCount = part?.reviewCount || 0;
  const isFounderMember = part?.isFounderMember;

  useEffect(() => {
    if (user && isAuthenticated) {
      dispatch(fetchSavedParts({ userId: user?.uid }));
    }
  }, []);

  if (!part?.sortOrder === 3) return;

  return (
    <motion.div
      variants={variants}
      className="h-full"
      initial="hidden"
      animate="visible"
    >
      <Card
        onClick={() => navigate(`/partDetail/${part?.id}`)}
        className="overflow-hidden hover:shadow-primary/20 cursor-pointer group hover:border-primary hover:shadow-lg transition-shadow duration-300 ease-in-out border h-full flex flex-col bg-card"
      >
        <CardHeader className="p-0 relative">
          <div className="aspect-[4/3] bg-secondary overflow-hidden relative">
            <PartImageSlider
              enableSlider={false}
              images={
                part?.imageUrls?.length
                  ? part.imageUrls
                  : part?.imageReferences?.length
                    ? part.imageReferences
                    : part?.imageUrl
                      ? [part.imageUrl]
                      : []
              }
              alt={`Image of ${partName} for ${partMakeModel} ${partYear}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            {tier && (
              <span
                className={`absolute top-1.5 right-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tier.bgColor} ${tier.textColor} ${tier.borderColor}`}
              >
                <tier.icon className="mr-1 h-2.5 w-2.5" />
                {tier.label}
              </span>
            )}
            <span className="absolute bottom-1.5 left-1.5 bg-primary/80 backdrop-blur-sm text-primary-foreground text-xs font-semibold px-1.5 py-0.5 rounded">
              ${displayPrice}
            </span>
            {isFounderMember && (
              <span
                className={`absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] text-white font-semibold border border-yellow-100 bg-yellow-600`}
              >
                Founding Seller
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 flex-grow">
          <div className="flex justify-between items-center">
            <h3
              className="text-xs sm:text-sm font-semibold mb-0.5 group-hover:text-primary transition-colors leading-tight line-clamp-2"
              title={`${partMakeModel} ${partYear} ${partName}`}
            >
              {partMakeModel} {partYear} {partName}
            </h3>

            {user?.role === "buyer" && isAuthenticated && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveClick(part);
                }}
                disabled={saveLoading || removeLoading}
                className="transition-transform hover:scale-110"
              >
                {isSaved ? (
                  <Bookmark className="w-6 h-6 text-primary fill-primary drop-shadow-md" />
                ) : (
                  <Bookmark className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors duration-300" />
                )}
              </button>
            )}
          </div>
          {/* <p className="text-[11px] sm:text-xs text-muted-foreground mb-1 truncate">
            {partYear} <br />
            {partMakeModel}
          </p> */}
          <div className="text-sm py-1 text-muted-foreground line-clamp-2 overflow-hidden text-ellipsis">
            {part?.description || `High quality used ${partName || "part"}.`}
          </div>
          <div className="flex items-center text-[10px] sm:text-[11px] text-muted-foreground mb-1 truncate">
            <Building
              className={`w-3 h-3 mr-1 text-muted-foreground ${
                part?.sellerLogo ? "hidden" : "inline-block"
              }`}
            />
            {sellerName || "Unknown Seller"}
          </div>
          {sellerRating && (
            <div className="flex items-center gap-0.5">
              <StarRating rating={sellerRating} size={12} />
              <span className="text-[10px] sm:text-[11px] text-muted-foreground">
                ({reviewCount})
              </span>
            </div>
          )}
        </CardContent>
        {user?.role === "seller" && user?.uid === part?.sellerId ? null : (
          <CardFooter className="p-3 pt-1.5 border-t flex flex-col gap-2 border-border/50 mt-auto">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleChatClick();
              }}
              variant="outline"
              size="sm"
              className="w-full group"
            >
              <MessageSquare className="mr-1.5 h-3 w-3 group-hover:animate-pulse" />
              Chat
            </Button>
            {part?.stripePaymentLink &&
              part?.stripePaymentLink !== "" &&
              user?.role !== "seller" && (
                <Button
                  variant="default"
                  size="sm"
                  className="w-full group bg-primary text-white hover:bg-primary/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBuyClick();
                  }}
                >
                  <ShoppingCart className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                  Buy Now
                </Button>
              )}
          </CardFooter>
        )}
      </Card>
      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}
      {showPaymentModal && (
        <PartPaymentModal
          open={showPaymentModal}
          part={part}
          close={() => setShowPaymentModal(false)}
        />
      )}
    </motion.div>
  );
};

export default SuggestedPartCard;
